import { AppLayout } from '@/components/app-layout';
import { AppIcon } from '@/components/ui/app-icon';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { iconSpinnerThird } from '@/lib/icons';
import { PRIMARY_BUTTON } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useFeatureFlagEnabled, usePostHog } from 'posthog-js/react';

import {
  useGetCancelIntentQuery,
  useSubmitCancellationFeedbackMutation,
} from '@/store/features/billing.feature';
import { BRAND } from '@/lib/brand';

/**
 * Where a customer lands after Stripe's cancel flow.
 *
 * Stripe asks its own one-question survey, but its sequence is fixed (offer,
 * then cancel, then reason) and the reason list is a closed enum we cannot
 * reword. By the time Stripe redirects here the answer is already attached to
 * the subscription, so this page reads it back and asks ONE specific follow-up
 * instead of a generic one. A prompt that names the customer's own stated
 * reason gets a concrete incident; "any other feedback?" gets a shrug.
 *
 * Nothing here is required, and the cancellation is already committed before
 * this page renders. That ordering is deliberate: gating cancellation behind a
 * survey is the exact pattern California AB 2863 and Colorado SB 25-145
 * target.
 */

// Keyed on Stripe's cancellation_reason enum.
const PROMPTS: Record<string, { question: string; placeholder: string }> = {
  unused: {
    question: 'What got in the way of using it?',
    placeholder:
      'e.g. we never got the list imported and it slipped down the priority list.',
  },
  low_quality: {
    question: 'Which records were wrong, and what did you check them against?',
    placeholder:
      'e.g. EU direct dials came back as switchboards. I checked twenty against LinkedIn.',
  },
  missing_features: {
    question: `What did you need ${BRAND.name} to do?`,
    placeholder: 'e.g. push enriched contacts straight into our sequencer.',
  },
  switched_service: {
    question: 'What did you move to, and what does it do better?',
    placeholder: 'e.g. moved to X. Their mobile numbers were more reliable.',
  },
  too_expensive: {
    question: `At what price would ${BRAND.name} have been worth keeping?`,
    placeholder:
      'e.g. it would have been worth $X a month at the volume we ran.',
  },
};

const FALLBACK_PROMPT = {
  question: `What were you trying to do that ${BRAND.name} didn't do?`,
  placeholder: 'e.g. build a list of heads of ops at Canadian SaaS companies.',
};

// /plans declares four optional search params, and TanStack requires the keys
// to be present on every navigate.
const PLANS_SEARCH = {
  credits_added: undefined,
  plan_name: undefined,
  status: undefined,
  tab: undefined,
} as const;

const formatPeriodEnd = (raw: string | null): string | null => {
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  // The API's UTC day, so it matches Plans and the plan-change refusal.
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

const CancelledPage = () => {
  const { i: intentId } = Route.useSearch();
  const navigate = useNavigate();
  const posthog = usePostHog();
  // Plans only offers Renew with this toggle on, so only then point there.
  const canRenew = useFeatureFlagEnabled('plan_renew_v1') === true;

  const { data, isLoading, isError } = useGetCancelIntentQuery(intentId!, {
    skip: !intentId,
  });
  const [submitFeedback, { isLoading: isSubmitting }] =
    useSubmitCancellationFeedbackMutation();

  const [freeText, setFreeText] = useState('');
  const [done, setDone] = useState(false);

  const prompt =
    (data?.feedback ? PROMPTS[data.feedback] : undefined) ?? FALLBACK_PROMPT;

  // They backed out of the cancel flow. Nothing happened, so say nothing and
  // put them back where they started.
  useEffect(() => {
    if (data?.state === 'unchanged') {
      navigate({ to: '/plans', search: PLANS_SEARCH, replace: true });
    }
  }, [data?.state, navigate]);

  // No intent id (a bookmarked or hand-typed URL) means there is no
  // cancellation to talk about.
  useEffect(() => {
    if (!intentId)
      navigate({ to: '/plans', search: PLANS_SEARCH, replace: true });
  }, [intentId, navigate]);

  const handleSubmit = async () => {
    const text = freeText.trim();
    if (!text || !intentId) return;
    try {
      await submitFeedback({
        intent_id: intentId,
        free_text: text,
        prompt: prompt.question,
      }).unwrap();
      posthog?.capture('cancel_survey_free_text_submitted', {
        feedback: data?.feedback ?? null,
      });
    } catch {
      // Losing a verbatim must never leave the customer stuck on this page.
    } finally {
      setDone(true);
    }
  };

  const handleSkip = () => {
    posthog?.capture('cancel_survey_skipped', {
      feedback: data?.feedback ?? null,
    });
    navigate({ to: '/plans', search: PLANS_SEARCH, replace: true });
  };

  if (!intentId || isLoading || data?.state === 'unchanged') {
    return (
      <div className='flex flex-1 items-center justify-center py-8'>
        <AppIcon
          icon={iconSpinnerThird}
          spin
          className='text-muted-foreground'
          style={{ fontSize: 20 }}
        />
      </div>
    );
  }

  // They took the retention offer. No survey on this screen: they stayed.
  if (data?.state === 'saved') {
    return (
      <div className='flex flex-1 items-center justify-center py-8'>
        <div className='mx-4 w-full max-w-md space-y-5'>
          <h1 className='text-2xl font-bold text-foreground'>
            Your 25% discount is applied.
          </h1>
          <p className='text-muted-foreground'>
            {data.plan_name ?? 'Your plan'} is 25% off for the next three
            months, then back to the usual price. Nothing else changed and your
            credits are untouched.
          </p>
          <button
            type='button'
            onClick={() => navigate({ to: '/plans', search: PLANS_SEARCH })}
            className={cn(PRIMARY_BUTTON, 'px-6 py-2.5 text-sm font-medium')}
          >
            Back to {BRAND.name}
          </button>
        </div>
      </div>
    );
  }

  const periodEnd = formatPeriodEnd(data?.period_end ?? null);

  return (
    <div className='flex flex-1 items-center justify-center py-8'>
      <div className='mx-4 w-full max-w-md space-y-6'>
        <div>
          <h1 className='text-2xl font-bold text-foreground'>
            Your plan is cancelled.
          </h1>
          <p className='mt-2 text-muted-foreground'>
            {isError ? (
              <>
                Your credits stay in your account and your lists are still
                there. You can restart any time from Plans.
              </>
            ) : (
              <>
                You keep {data?.plan_name ?? 'your plan'}
                {periodEnd
                  ? ` until ${periodEnd}`
                  : ' until the end of your billing period'}
                . Your credits stay in your account and your lists are still
                there.{' '}
                {periodEnd && canRenew
                  ? 'You can renew your plan from Plans before then.'
                  : 'You can restart any time from Plans.'}
              </>
            )}
          </p>
        </div>

        {done ? (
          <div className='rounded-lg border border-border bg-card px-4 py-5'>
            <p className='text-sm text-foreground'>Thank you. That helps.</p>
            <button
              type='button'
              onClick={() => navigate({ to: '/plans', search: PLANS_SEARCH })}
              className='mt-3 text-sm text-primary hover:underline'
            >
              Back to {BRAND.name}
            </button>
          </div>
        ) : (
          <div className='space-y-3'>
            <div>
              <p className='text-sm font-medium text-foreground'>
                {prompt.question}
              </p>
              <p className='mt-1 text-xs text-muted-foreground'>
                Optional. One field, and a specific example is more useful than
                a general one.
              </p>
            </div>

            <textarea
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              placeholder={prompt.placeholder}
              rows={4}
              maxLength={4000}
              className='w-full rounded-lg border border-border bg-card px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary focus:outline-hidden'
            />

            <div className='flex items-center gap-3'>
              <button
                type='button'
                onClick={handleSubmit}
                disabled={!freeText.trim() || isSubmitting}
                className={cn(
                  PRIMARY_BUTTON,
                  'px-6 py-2.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50'
                )}
              >
                {isSubmitting ? 'Sending...' : 'Send'}
              </button>
              <button
                type='button'
                onClick={handleSkip}
                className='text-sm text-muted-foreground hover:text-foreground'
              >
                Skip
              </button>
            </div>
          </div>
        )}

        <p className='text-xs text-muted-foreground'>
          Or reply to{' '}
          <a
            href='mailto:victor@cleanlist.ai'
            className='text-primary hover:underline'
          >
            victor@cleanlist.ai
          </a>
          . It reaches a founder.
        </p>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/cancelled')({
  validateSearch: (search: Record<string, unknown>) => ({
    i: typeof search.i === 'string' && search.i ? search.i : undefined,
  }),
  // In the app's layout, like every other page, so the sidebar never covers it.
  component: () => (
    <AppLayout variant='full-bleed'>
      <CancelledPage />
    </AppLayout>
  ),
});
